import {
  OptimizationEmployeeLeaving,
  OptimizationSoftwareAccountDuplicate,
  OptimizationSoftwareAccountRedundantLicense,
  OptimizationSoftwareAccountUnused,
  OptimizationRightsizing,
} from '@/client'
import { db } from '../database'

export type OptimizationTypes =
  | OptimizationEmployeeLeaving
  | OptimizationSoftwareAccountDuplicate
  | OptimizationSoftwareAccountRedundantLicense
  | OptimizationSoftwareAccountUnused
  | OptimizationRightsizing

export interface OptimizationTable {
  _id: string // Unique identifier, possibly a UUID
  created: string // ISO 8601 formatted date-time string
  updated: string // ISO 8601 formatted date-time string
  deleted: number // Boolean stored as integer (0 or 1)
  deleted_at: string | null // Nullable ISO 8601 date-time string
  deprecated: number // Boolean stored as integer (0 or 1)
  hidden: number // Boolean stored as integer (0 or 1)
  tags: string // JSON string representing an array of strings
  type: string
  data: string // JSON string representing OptimizationEmployeeLeavingData, OptimizationSoftwareAccountDuplicateData, OptimizationSoftwareAccountRedundantLicenseData or OptimizationSoftwareAccountUnusedData
  references: string // JSON string representing OptimizationEmployeeLeavingData, OptimizationSoftwareAccountDuplicateData, OptimizationSoftwareAccountRedundantLicenseData or OptimizationSoftwareAccountUnusedData
}

export interface OptimizationSavingsTable {
  _id: string // Unique identifier, possibly a UUID
  savings: number
}

export async function createOptimizationTable() {
  await db.schema
    .createTable('optimization')
    .ifNotExists()
    .addColumn('_id', 'text', (col) => col.primaryKey().notNull())
    .addColumn('created', 'text', (col) => col.notNull()) // ISO 8601 string
    .addColumn('updated', 'text', (col) => col.notNull()) // ISO 8601 string
    .addColumn('deleted', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('deleted_at', 'text') // Nullable ISO 8601 string
    .addColumn('deprecated', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('hidden', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('tags', 'text', (col) => col.notNull()) // JSON string
    .addColumn('type', 'text')
    .addColumn('data', 'text', (col) => col.notNull()) // JSON string
    .addColumn('references', 'text', (col) => col.notNull()) // JSON string
    .execute()

  // Index auf der Spalte 'type' erstellen
  await db.schema
    .createIndex('idx_optimization_type')
    .ifNotExists()
    .on('optimization')
    .column('type')
    .execute()
}

export async function createOptimizationSavingsTable() {
  await db.schema
    .createTable('optimization_savings')
    .ifNotExists()
    .addColumn('_id', 'text', (col) => col.primaryKey().notNull())
    .addColumn('savings', 'integer', (col) => col.notNull())
    .execute()

  // Index auf der Spalte 'savings' erstellen
  await db.schema
    .createIndex('idx_optimization_saving')
    .ifNotExists()
    .on('optimization_savings')
    .column('savings')
    .execute()
}

// Helper function to serialize Optimization for database storage
export function serializeOptimization(
  optimization: OptimizationTypes
): OptimizationTable {
  return {
    _id: optimization._id,
    created: optimization.created,
    updated: optimization.updated,
    deleted: optimization.deleted ? 1 : 0,
    deleted_at: optimization.deleted_at ?? null,
    deprecated: optimization.deprecated ? 1 : 0,
    hidden: optimization.hidden ? 1 : 0,
    tags: JSON.stringify(optimization.tags),
    type: optimization.type,
    data: JSON.stringify(optimization.data),
    references: JSON.stringify(optimization.references),
  }
}

// Helper function to deserialize database record into Optimization
export function deserializeOptimization(
  record: OptimizationTable
): OptimizationTypes {
  return {
    ...record,
    deleted: Boolean(record.deleted),
    deleted_at: record.deleted_at ?? null,
    deprecated: Boolean(record.deprecated),
    hidden: Boolean(record.hidden),
    tags: JSON.parse(record.tags),
    data: JSON.parse(record.data),
    references: JSON.parse(record.references),
    // joined data needs to be parsed as well
    licenses: JSON.parse(record.licenses),
  }
}
